/**
 * ImageAttachment Component
 * 
 * Reusable component for attaching images to messages and comments
 * Handles file selection, preview, upload, and display
 */

import React, { useState, useRef } from 'react';
import { Image, X, Upload, Loader } from 'lucide-react';
import api from '../lib/api';

export default function ImageAttachment({ 
  onAttachmentsChange, 
  attachments = [],
  maxFiles = 5,
  uploadEndpoint = '/api/messages/upload-attachment',
  metadata = {} // Additional metadata like assetId, repairId
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    // Check if adding these files would exceed max
    if (attachments.length + files.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate file types (images only)
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      setError('Only image files are allowed');
      return;
    }

    // Validate file sizes (10MB max per file)
    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Files must be less than 10MB');
      return;
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Create FormData
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      // Add metadata
      Object.keys(metadata).forEach(key => {
        formData.append(key, metadata[key]);
      });

      // Upload files
      const result = await api.upload(
        uploadEndpoint,
        formData,
        (progress) => setUploadProgress(progress)
      );

      if (result.files && result.files.length > 0) {
        // Add uploaded files to attachments
        const newAttachments = [...attachments, ...result.files];
        onAttachmentsChange(newAttachments);
      }

      if (result.errors && result.errors.length > 0) {
        setError(`Some files failed to upload: ${result.errors.map(e => e.filename).join(', ')}`);
      }

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    onAttachmentsChange(newAttachments);
  };

  // Trigger file input click
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      {/* File Input (hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading || attachments.length >= maxFiles}
      />

      {/* Upload Button */}
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={uploading || attachments.length >= maxFiles}
        className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        title={attachments.length >= maxFiles ? `Maximum ${maxFiles} files` : 'Attach images'}
      >
        {uploading ? (
          <>
            <Loader size={16} className="text-blue-400 animate-spin" />
            <span className="text-slate-300">Uploading... {uploadProgress}%</span>
          </>
        ) : (
          <>
            <Image size={16} className="text-slate-400" />
            <span className="text-slate-300">
              Attach Images ({attachments.length}/{maxFiles})
            </span>
          </>
        )}
      </button>

      {/* Error Message */}
      {error && (
        <div className="text-xs text-red-400 bg-red-900/20 border border-red-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}

      {/* Attachment Previews */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className="relative group w-20 h-20 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden"
            >
              <img
                src={attachment.url}
                alt={attachment.filename}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemoveAttachment(index)}
                className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <X size={12} className="text-white" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 text-[10px] text-white truncate">
                {attachment.filename}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * AttachmentDisplay Component
 * 
 * Display attachments in messages/comments (read-only)
 */
export function AttachmentDisplay({ attachments = [], className = '' }) {
  const [lightboxImage, setLightboxImage] = useState(null);

  if (!attachments || attachments.length === 0) return null;

  return (
    <>
      <div className={`flex flex-wrap gap-2 mt-2 ${className}`}>
        {attachments.map((attachment, index) => (
          <div
            key={index}
            className="relative w-32 h-32 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 transition-colors"
            onClick={() => setLightboxImage(attachment)}
          >
            <img
              src={attachment.url}
              alt={attachment.filename || 'Attachment'}
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1 text-[10px] text-white truncate">
              {attachment.filename || 'Image'}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 bg-slate-800 hover:bg-slate-700 rounded-full transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
          <img
            src={lightboxImage.url}
            alt={lightboxImage.filename || 'Attachment'}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-4 right-4 text-center">
            <p className="text-white text-sm bg-black/60 rounded px-4 py-2 inline-block">
              {lightboxImage.filename || 'Image'}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

